import { useDashboardIdentity } from './useDashboardIdentity';
import { useQuery } from '@tanstack/react-query';
import type { StartTimeoutsResponse, StartTimeoutRange } from '@evi/shared';
import { fetchStartTimeouts } from '@/lib/api';

/**
 * Two-Minute Window 4.5 — start-timeout rate data hook. Shared by the
 * dashboard widget, the Chargers and Locations tables and the detail
 * pages, keyed on the range so the 30d table and a 7d widget can coexist.
 * Live: session and charger events refetch it through
 * LiveUpdatesProvider; no poll.
 *
 * The endpoint is gated on Dashboard.read (server-side matrix); the
 * Chargers / Locations pages are also open to personas without it
 * (charger owners, fleet admins), and the client cannot resolve the
 * matrix itself. So: no retry; one 403, one dash in the column.
 */
export function useStartTimeouts(range: StartTimeoutRange = '30d') {
  const identity = useDashboardIdentity();
  return useQuery<StartTimeoutsResponse>({
    queryKey: ['dashboard', identity, 'start-timeouts', range],
    queryFn: () => fetchStartTimeouts(range),
    staleTime: 60_000,
    retry: false,
  });
}

/** "9 of 12 starts · 75%" — the one way the number is written everywhere. */
export function formatTimeoutSummary(
  row: { starts: number; timed_out: number; late_cancel: number; timeout_rate: number | null },
): string {
  if (row.starts === 0 || row.timeout_rate === null) return 'no starts';
  const timeouts = row.timed_out + row.late_cancel;
  return `${timeouts} of ${row.starts} start${row.starts === 1 ? '' : 's'} · ${Math.round(row.timeout_rate * 100)}%`;
}

/** Whole days since an ISO timestamp, null when absent. */
export function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  const ms = Date.now() - Date.parse(iso);
  return Number.isFinite(ms) ? Math.max(0, Math.floor(ms / 86_400_000)) : null;
}

/** "cable in another pedestal 7 of 9" — the diagnosis line, empty when no timeouts. */
export function siblingCableLabel(row: { timed_out: number; late_cancel: number; sibling_cable: number }): string {
  const timeouts = row.timed_out + row.late_cancel;
  if (timeouts === 0) return '';
  return `cable in another pedestal ${row.sibling_cable} of ${timeouts}`;
}

/** "no contact 157d" / "no contact 3h" from an ISO last-heartbeat; "never connected" when none.
 *  Worded as contact, not "offline": the Status column reads the socket flag,
 *  this reads the heartbeat, and the two can honestly disagree. */
export function offlineLabel(offlineSince: string | null): string {
  if (!offlineSince) return 'never connected';
  const ms = Date.now() - Date.parse(offlineSince);
  if (!Number.isFinite(ms)) return 'no contact';
  const totalMin = Math.floor(ms / 60_000);
  if (totalMin < 60) return `no contact ${totalMin}m`;
  const h = Math.floor(totalMin / 60);
  if (h < 24) return `no contact ${h}h`;
  return `no contact ${Math.floor(h / 24)}d`;
}

/** "Last completed charge 103 days ago" / "No completed charge on record". */
export function lastChargeLabel(iso: string | null): string {
  const d = daysSince(iso);
  if (d === null) return 'No completed charge on record';
  if (d === 0) return 'Last completed charge today';
  return `Last completed charge ${d} day${d === 1 ? '' : 's'} ago`;
}
